import React, { useEffect, useState } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Sparkles, AlertCircle, TrendingUp, TrendingDown, DollarSign, Factory as FactoryIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

const AIInsights = () => {
  const [insights, setInsights] = useState([]);
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Indicadores e Desempenho — NautilusOS";
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      const response = await api.get('/ai/insights');
      setInsights(response.data.insights);
      setSummary(response.data.summary);
    } catch (error) {
      toast.error('Erro ao carregar insights');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'critical': 'bg-red-100 text-red-800 border-red-300',
      'high': 'bg-orange-100 text-orange-800 border-orange-300',
      'medium': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'low': 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colors[severity] || colors['low'];
  };

  const getSeverityIcon = (severity) => {
    if (severity === 'critical' || severity === 'high') {
      return <AlertCircle size={20} className="text-red-600" />;
    }
    return <TrendingUp size={20} className="text-blue-600" />;
  };

  const getAreaIcon = (area) => {
    const icons = {
      'produção': <FactoryIcon size={20} />,
      'producao': <FactoryIcon size={20} />,
      'production': <FactoryIcon size={20} />,
      'custos': <DollarSign size={20} />,
      'costs': <DollarSign size={20} />,
      'estoque': <TrendingDown size={20} />,
      'stock': <TrendingDown size={20} />,
      'recursos': <TrendingUp size={20} />,
      'resources': <TrendingUp size={20} />
    };
    return icons[area?.toLowerCase()] || <Sparkles size={20} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-insights-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <Sparkles className="text-purple-600" size={32} />
            Insights de IA
          </h1>
          <p className="text-gray-600 mt-1">Análise inteligente das operações</p>
        </div>
      </div>

      {summary && (
        <Card className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200" data-testid="insights-summary">
          <CardContent className="p-6">
            <p className="text-gray-700">{summary}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6">
        {insights.map((insight, index) => (
          <Card
            key={item.id || item.title || index}
            className={`border-2 ${getSeverityColor(insight.severity)} hover:shadow-lg transition-shadow`}
            data-testid={`insight-card-${index}`}
          >
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <div className="p-2 bg-white rounded-lg">
                    {getSeverityIcon(insight.severity)}
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-xl mb-1">{insight.title}</CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge className={getSeverityColor(insight.severity)}>
                        {insight.severity === 'critical' && 'Crítico'}
                        {insight.severity === 'high' && 'Alto'}
                        {insight.severity === 'medium' && 'Médio'}
                        {insight.severity === 'low' && 'Baixo'}
                      </Badge>
                      <Badge variant="outline" className="flex items-center gap-1">
                        {getAreaIcon(insight.affected_area)}
                        {insight.affected_area}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-sm mb-2">Descrição:</h4>
                <p className="text-gray-700">{insight.description}</p>
              </div>
              <div className="bg-white p-4 rounded-lg border-l-4 border-blue-600">
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <Sparkles size={16} className="text-blue-600" />
                  Recomendação de IA:
                </h4>
                <p className="text-gray-700">{insight.recommendation}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {insights.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Sparkles size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">Nenhum insight disponível no momento.</p>
            <p className="text-sm text-gray-500 mt-2">A IA está analisando seus dados continuamente.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AIInsights;
